import React, { useCallback, useEffect, useState } from 'react';

import 'react-datepicker/dist/react-datepicker.css';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

import { FaDownload, FaFileInvoice, FaListUl, FaCoins } from 'react-icons/fa';

import api from '../../services/api';

import ActivityIndicator from '../../components/ActivityIndicator';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import readBlobAsText from '../../utils/readBlobAsText';

import {
  Aviso,
  CardItem,
  Cards,
  Container,
  DownloadButton,
  LoadingContainer,
  MonthSelect,
  StatusBadge,
  StatusList,
  Toolbar,
  Value,
  Wrapper,
} from './styles';

registerLocale('pt-BR', pt);

interface ResumoHistorico {
  mes: string;
  dataInicio: string;
  dataFim: string;
  quantidade: number;
  valorTotal: number;
  porStatus: Record<string, number>;
}

/** Rótulo amigável por status (espelha NotasEmitidas). */
const STATUS_LABEL: Record<string, string> = {
  authorized: 'Autorizadas',
  pending: 'Pendentes',
  processing: 'Processando',
  rejected: 'Rejeitadas',
  cancelled: 'Canceladas',
  canceled: 'Canceladas',
  draft: 'Rascunho',
};

const corDoStatus = (
  status: string,
): 'verde' | 'vermelho' | 'amarelo' | 'cinza' => {
  const s = status.toLowerCase();
  if (s === 'authorized' || s === 'autorizada') return 'verde';
  if (['rejected', 'cancelled', 'canceled'].includes(s)) return 'vermelho';
  if (['pending', 'processing'].includes(s)) return 'amarelo';
  return 'cinza';
};

const formatarValor = (valor: number): string =>
  (valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

/** `Date` → `YYYY-MM` (componentes locais, sem drift de fuso). */
const toMesParam = (data: Date): string => format(data, 'yyyy-MM');

const HistoricoContador: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [mes, setMes] = useState<Date>(() => new Date());
  const [resumo, setResumo] = useState<ResumoHistorico | null>(null);
  const [loading, setLoading] = useState(true);
  const [baixando, setBaixando] = useState(false);

  const empresaId = user.empresa_id;

  const carregarResumo = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await api.get(
        `/companies/${empresaId}/nfce/historico-contador/resumo`,
        { params: { mes: toMesParam(mes) } },
      );
      setResumo(response.data);
    } catch (err) {
      setResumo(null);
      addToast({
        type: 'error',
        title: 'Erro ao carregar o resumo',
        description: 'Não foi possível consultar o histórico do mês.',
      });
    } finally {
      setLoading(false);
    }
  }, [empresaId, mes, addToast]);

  useEffect(() => {
    carregarResumo();
  }, [carregarResumo]);

  const baixarZip = useCallback(async (): Promise<void> => {
    setBaixando(true);
    const mesParam = toMesParam(mes);
    try {
      const response = await api.get(
        `/companies/${empresaId}/nfce/historico-contador`,
        { params: { mes: mesParam }, responseType: 'blob' },
      );

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/zip' }),
      );
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `nfce-historico-${mesParam}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      addToast({
        type: 'success',
        title: 'Download iniciado',
        description: `Histórico de ${mesParam} (.zip) gerado com sucesso.`,
      });
    } catch (err) {
      // O corpo de erro chega como Blob (responseType: 'blob'); extrai a
      // mensagem normalizada do backend (ex.: empresa não habilitada/mes
      // inválido) quando houver — padrão NotasEmitidas.
      let description =
        'Não foi possível gerar o .zip do mês selecionado.';
      const data = (err as any)?.response?.data;
      if (data instanceof Blob) {
        try {
          const texto = await readBlobAsText(data);
          const msg = JSON.parse(texto)?.message;
          if (msg) description = msg;
        } catch {
          // mantém a mensagem genérica
        }
      }
      addToast({
        type: 'error',
        title: 'Erro ao baixar o histórico',
        description,
      });
    } finally {
      setBaixando(false);
    }
  }, [empresaId, mes, addToast]);

  const autorizadas = resumo?.porStatus?.authorized ?? 0;
  const statusEntries = Object.entries(resumo?.porStatus ?? {});

  return (
    <Container>
      <Wrapper>
        <header>
          <div>
            <h1>Histórico do Contador</h1>
            <h3>
              Resumo mensal das NFC-e e download do lote de XMLs autorizados (+
              resumo.csv) em um clique
            </h3>
          </div>

          <Toolbar>
            <MonthSelect>
              <label htmlFor="mes-historico-contador">Mês</label>
              <ReactDatePicker
                id="mes-historico-contador"
                selected={mes}
                onChange={(date: Date | null) => date && setMes(date)}
                locale="pt-BR"
                dateFormat="MM/yyyy"
                showMonthYearPicker
                maxDate={new Date()}
              />
            </MonthSelect>

            <DownloadButton
              type="button"
              onClick={baixarZip}
              disabled={baixando}
            >
              <FaDownload />
              {baixando ? 'Gerando...' : 'Baixar .zip'}
            </DownloadButton>
          </Toolbar>
        </header>

        {loading ? (
          <LoadingContainer>
            <ActivityIndicator />
          </LoadingContainer>
        ) : (
          <>
            <Cards>
              <CardItem>
                <header>
                  <h2>Notas no mês</h2>
                  <FaFileInvoice />
                </header>
                <main>
                  <Value>{resumo?.quantidade ?? 0}</Value>
                </main>
                <footer>
                  <span>{autorizadas} autorizadas serão exportadas</span>
                </footer>
              </CardItem>

              <CardItem>
                <header>
                  <h2>Valor total</h2>
                  <FaCoins />
                </header>
                <main>
                  <Value style={{ fontSize: 30 }}>
                    {formatarValor(resumo?.valorTotal ?? 0)}
                  </Value>
                </main>
                <footer>
                  <span>Soma dos totais do período</span>
                </footer>
              </CardItem>

              <CardItem>
                <header>
                  <h2>Por status</h2>
                  <FaListUl />
                </header>
                <main>
                  {statusEntries.length === 0 ? (
                    <span style={{ fontSize: 14 }}>Nenhuma nota no mês</span>
                  ) : (
                    <StatusList>
                      {statusEntries.map(([status, qtd]) => (
                        <StatusBadge key={status} cor={corDoStatus(status)}>
                          {STATUS_LABEL[status.toLowerCase()] || status}: {qtd}
                        </StatusBadge>
                      ))}
                    </StatusList>
                  )}
                </main>
                <footer>
                  <span>Quebra por status no período</span>
                </footer>
              </CardItem>
            </Cards>

            <Aviso>
              O <strong>.zip</strong> contém somente as NFC-e{' '}
              <strong>autorizadas</strong> do mês ({autorizadas} de{' '}
              {resumo?.quantidade ?? 0}) mais o <strong>resumo.csv</strong>.
            </Aviso>
          </>
        )}
      </Wrapper>
    </Container>
  );
};

export default HistoricoContador;
